import { Platform } from 'react-native';
import Constants from 'expo-constants';

const isWeb = Platform.OS === 'web';

const getAdsConfig = () => {
  const extra =
    Constants?.expoConfig?.extra ??
    Constants?.manifest?.extra ??
    {};
  return extra?.ads ?? {};
};

const { adsenseClientId = '', adsenseFullscreenSlotId = '' } = getAdsConfig();

const ADSENSE_CLIENT_ID = adsenseClientId;
const ADSENSE_SLOT_ID = adsenseFullscreenSlotId;
const DEFAULT_MINIMUM_WATCH_MS = 8000;

let adsenseScriptPromise = null;

const ensureAdsenseScript = () => {
  if (!isWeb) {
    return Promise.resolve();
  }

  if (!ADSENSE_CLIENT_ID) {
    return Promise.reject(
      new Error('AdSense client ID is missing. Define ADSENSE_CLIENT_ID in env.')
    );
  }

  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Document is not available.'));
  }

  if (adsenseScriptPromise) {
    return adsenseScriptPromise;
  }

  // Script'in zaten yüklenip yüklenmediğini kontrol et
  const existingScript = document.querySelector(
    `script[src*="adsbygoogle.js"]`
  );
  if (existingScript) {
    return Promise.resolve();
  }

  // window.adsbygoogle zaten varsa script yüklenmiş demektir
  if (typeof window !== 'undefined' && window.adsbygoogle) {
    return Promise.resolve();
  }

  adsenseScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.crossOrigin = 'anonymous';
    // data-adsense-script attribute'unu kaldırdık - AdSense desteklemiyor
    script.onload = () => resolve();
    script.onerror = (error) => {
      console.error('AdSense script load failed:', error);
      adsenseScriptPromise = null;
      reject(error);
    };
    document.head.appendChild(script);
  });

  return adsenseScriptPromise;
};

export const ensureAdsenseInitialized = () => {
  if (!isWeb || !ADSENSE_CLIENT_ID) {
    return;
  }

  ensureAdsenseScript().catch((error) => {
    console.warn('AdSense initialization failed:', error?.message || error);
  });
};

const createOverlayElements = ({ minimumWatchMs }) => {
  const overlay = document.createElement('div');
  overlay.style.position = 'fixed';
  overlay.style.inset = '0';
  overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.88)';
  overlay.style.backdropFilter = 'blur(2px)';
  overlay.style.display = 'flex';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.style.zIndex = '2147483647';
  overlay.style.padding = '16px';

  const panel = document.createElement('div');
  panel.style.width = 'min(440px, 95vw)';
  panel.style.maxWidth = '480px';
  panel.style.height = 'min(640px, 95vh)';
  panel.style.maxHeight = '90vh';
  panel.style.background = '#050505';
  panel.style.borderRadius = '18px';
  panel.style.padding = '16px';
  panel.style.boxShadow = '0 25px 80px rgba(0, 0, 0, 0.65)';
  panel.style.display = 'flex';
  panel.style.flexDirection = 'column';
  panel.style.gap = '12px';

  const title = document.createElement('div');
  title.innerText = 'Reklam yükleniyor...';
  title.style.color = '#ffffff';
  title.style.fontSize = '18px';
  title.style.fontWeight = '600';
  title.style.textAlign = 'center';

  const adRegion = document.createElement('div');
  adRegion.style.flex = '1';
  adRegion.style.width = '100%';
  adRegion.style.background = '#111111';
  adRegion.style.borderRadius = '12px';
  adRegion.style.padding = '8px';
  adRegion.style.display = 'flex';
  adRegion.style.alignItems = 'center';
  adRegion.style.justifyContent = 'center';

  const adSlot = document.createElement('ins');
  adSlot.className = 'adsbygoogle';
  adSlot.style.display = 'block';
  adSlot.style.width = '100%';
  adSlot.style.height = '100%';
  adSlot.setAttribute('data-ad-client', ADSENSE_CLIENT_ID);
  adSlot.setAttribute('data-ad-slot', ADSENSE_SLOT_ID);
  adSlot.setAttribute('data-ad-format', 'auto');
  adSlot.setAttribute('data-full-width-responsive', 'true');

  adRegion.appendChild(adSlot);

  const closeButton = document.createElement('button');
  closeButton.innerText = `Reklamı izleyin (${Math.ceil(
    minimumWatchMs / 1000
  )} sn)`;
  closeButton.disabled = true;
  closeButton.style.padding = '14px';
  closeButton.style.border = 'none';
  closeButton.style.borderRadius = '999px';
  closeButton.style.fontSize = '16px';
  closeButton.style.fontWeight = '600';
  closeButton.style.cursor = 'not-allowed';
  closeButton.style.background =
    'linear-gradient(135deg, #E94560 0%, #FF6B6B 100%)';
  closeButton.style.color = '#ffffff';
  closeButton.style.transition = 'opacity 200ms ease';
  closeButton.style.opacity = '0.65';

  panel.appendChild(title);
  panel.appendChild(adRegion);
  panel.appendChild(closeButton);

  overlay.appendChild(panel);

  return { overlay, closeButton, adSlot, title };
};

export const isAdsenseConfigured = Boolean(ADSENSE_CLIENT_ID && ADSENSE_SLOT_ID);

export const showWebRewardedInterstitial = (
  options = {}
) => {
  if (!isWeb) {
    return Promise.reject(
      new Error('Web rewarded interstitial is only available on web.')
    );
  }

  if (!ADSENSE_CLIENT_ID || !ADSENSE_SLOT_ID) {
    return Promise.reject(
      new Error('AdSense credentials are missing. Configure env variables.')
    );
  }

  const minimumWatchMs =
    typeof options.minimumWatchMs === 'number'
      ? options.minimumWatchMs
      : DEFAULT_MINIMUM_WATCH_MS;

  return ensureAdsenseScript().then(
    () =>
      new Promise((resolve, reject) => {
        if (typeof document === 'undefined') {
          reject(new Error('Document is not available.'));
          return;
        }

        const { overlay, closeButton, adSlot, title } = createOverlayElements({
          minimumWatchMs,
        });

        const cleanup = () => {
          if (overlay?.parentNode) {
            overlay.parentNode.removeChild(overlay);
          }
        };

        closeButton.addEventListener('click', () => {
          if (closeButton.disabled) {
            return;
          }
          clearTimeout(fallbackEnableTimer);
          cleanup();
          resolve();
        });

        document.body.appendChild(overlay);

        const enableClose = () => {
          closeButton.disabled = false;
          closeButton.style.cursor = 'pointer';
          closeButton.style.opacity = '1';
          closeButton.innerText = 'Devam Et';
        };

        const fallbackEnableTimer = setTimeout(enableClose, minimumWatchMs);

        try {
          if (typeof window === 'undefined') {
            throw new Error('Window is not available.');
          }
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          title.innerText = 'Reklamı izleyerek +1 Sosyal Enerji kazan!';
        } catch (error) {
          console.error('AdSense render error:', error);
          clearTimeout(fallbackEnableTimer);
          cleanup();
          reject(error);
          return;
        }
      })
  );
};

